<template>
  <div>
    <!-- Use the existing WalletHeader component -->
    <wallet-header :walletData="walletData" />

    <!-- Withdrawal Request Form -->
    <div class="p-4 shadow-sm rounded withdraw-section">
      <div class="form-section withdraw-form">
        <h5 class="fw-bold">Withdrawal Request</h5>
        <p class="text-muted small">
          Enter your details below to withdraw money
        </p>

        <!-- Transaction Limits Info -->
        <div class="alert alert-info mb-4" v-if="walletData?.walletData">
          <div class="d-flex justify-content-between align-items-center">
            <div>
              <small class="fw-bold">Monthly Limit:</small>
              <span class="ms-2"
                >{{ currentMonthTransactions }} /
                {{ maxTransactionsPerMonth }}</span
              >
            </div>
            <div>
              <small class="fw-bold">Minimum:</small>
              <span class="ms-2"
                >{{ formatBalance(minimumTransaction) }}
                {{ walletCurrency }}</span
              >
            </div>
          </div>
          <div
            class="mt-2"
            v-if="remainingTransactions <= 3 && remainingTransactions > 0"
          >
            <small class="text-warning">
              ⚠️ {{ remainingTransactions }} transaction{{
                remainingTransactions === 1 ? "" : "s"
              }}
              remaining this month.
            </small>
          </div>
          <div class="mt-2" v-if="remainingTransactions === 0">
            <small class="text-danger">
              🚫 Monthly transaction limit reached. Try again next month.
            </small>
          </div>
        </div>
        <div v-if="successMessage" class="alert alert-success mb-4">
          {{ successMessage }}
        </div>

        <div v-if="errorMessage" class="alert alert-danger mb-4">
          {{ errorMessage }}
        </div>
        <form @submit.prevent="submitWithdrawal">
          <div class="mb-3">
            <label class="form-label amount-label">Enter amount</label>

            <input
              type="number"
              step="0.000001"
              :min="minimumTransaction"
              class="form-control amount-input"
              :placeholder="`Minimum: ${formatBalance(
                minimumTransaction
              )} ${walletCurrency}`"
              v-model="form.amount"
              :class="{ 'is-invalid': errors.amount }"
              @input="handleAmountInput"
              @keypress="restrictInput"
              :disabled="remainingTransactions === 0"
            />
            <small class="text-muted balance-info"
              >Available balance: {{ formatBalance(availableBalance) }}
              {{ walletCurrency }}</small
            >
            <div v-if="errors.amount" class="invalid-feedback">
              {{ errors.amount }}
            </div>
          </div>

          <div class="mb-3">
            <label class="form-label fw-semibold">Email</label>
            <input
              type="email"
              class="form-control"
              placeholder="johndoe@gmail.com"
              v-model="form.email"
              :class="{ 'is-invalid': errors.email }"
              disabled
              readonly
            />
            <div v-if="errors.email" class="invalid-feedback">
              {{ errors.email }}
            </div>
          </div>

          <div class="mb-3">
            <label class="form-label fw-semibold"
              >Binance wallet address / Id</label
            >
            <input
              type="text"
              class="form-control"
              placeholder="342957823"
              v-model="form.walletAddress"
              :class="{ 'is-invalid': errors.walletAddress }"
              disabled
              readonly
            />
            <div v-if="errors.walletAddress" class="invalid-feedback">
              {{ errors.walletAddress }}
            </div>
          </div>

          <div class="border-dashed my-4"></div>

          <div class="d-flex justify-content-between text-muted mb-3">
            <span>Amount to Receive</span>
            <span class="summary-amount"
              >{{ form.amount || "0.000000" }} Btc</span
            >
          </div>
          <div class="d-flex justify-content-between text-muted mb-3">
            <span>Processing Fee ({{ processingFeeRate }}%)</span>
            <span class="fee-amount">{{ calculateFee() }} Btc</span>
          </div>
          <div class="d-flex justify-content-between fw-bold mb-2">
            <span>Total Deducted from MyWhoosh Wallet</span>
            <span class="deducted-amount">{{ calculateTotal() }} Btc</span>
          </div>
          <div class="d-flex justify-content-between text-success fw-bold mb-3">
            <span>Total You Will Receive in Binance Wallet</span>
            <span class="total-amount"
              >{{ form.amount || "0.000000" }} Btc</span
            >
          </div>

          <div class="border-dashed my-4"></div>

          <div class="d-flex gap-3">
            <button
              type="submit"
              class="btn withdraw-btn px-4"
              :disabled="isSubmitting || !canWithdraw"
            >
              {{ isSubmitting ? "Processing..." : "Withdraw" }}
            </button>
            <button
              type="button"
              class="btn btn-outline-dark cancel-btn px-4 text-dark"
              @click="cancelWithdrawal"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import WalletHeader from "@/components/wallet/WalletHeader.vue";
import { mapActions } from "vuex";

export default {
  name: "WithdrawalRequest",
  components: {
    WalletHeader,
  },
  head: {
    link: [
      {
        rel: "stylesheet",
        href: "/assets/css/wallet.css",
      },
    ],
  },
  data() {
    return {
      walletData: null,
      form: {
        amount: "",
        email: "",
        walletAddress: "",
      },
      errors: {},
      isSubmitting: false,
      successMessage: "",
      errorMessage: "",
    };
  },
  computed: {
    availableBalance() {
      return parseFloat(this.walletData?.walletData?.balance || 0);
    },
    minimumTransaction() {
      return parseFloat(
        this.walletData?.walletData?.minimumTransaction || 0.000001
      );
    },
    maxTransactionsPerMonth() {
      return this.walletData?.walletData?.maxTransactionsPerMonth || 0;
    },
    currentMonthTransactions() {
      return this.walletData?.walletData?.currentMonthTransactions || 0;
    },
    remainingTransactions() {
      return this.walletData?.walletData?.remainingTransactions || 0;
    },
    walletCurrency() {
      return this.walletData?.walletData?.currency || "BTC";
    },
    processingFeeRate() {
      return parseFloat(this.walletData?.walletData?.processingFee || 0);
    },

    canWithdraw() {
      // Check if user is verified
      if (!this.walletData?.user?.verified) {
        return false;
      }

      // Check if there are any validation errors
      if (Object.keys(this.errors).length > 0) {
        return false;
      }

      // Check if amount is valid

      const amount = parseFloat(this.form.amount || 0);
      if (
        !this.form.amount ||
        amount <= 0 ||
        amount < this.minimumTransaction
      ) {
        return false;
      }

      // Check if user has sufficient balance including fees
      const balance = parseFloat(this.walletData?.walletData?.balance || 0);
      const fee = amount * (this.processingFeeRate / 100);
      const totalDeduction = amount + fee;

      return balance > 0 && totalDeduction <= balance;
    },
  },
  methods: {
    ...mapActions("user", ["getWalletData", "submitWithdrawalRequest"]),
    handleAmountInput() {
      // Small delay to ensure the v-model has updated
      this.$nextTick(() => {
        this.validateAmount();
      });
    },
    restrictInput(event) {
      // Allow: backspace, delete, tab, escape, enter
      if (
        [46, 8, 9, 27, 13].indexOf(event.keyCode) !== -1 ||
        // Allow: Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
        (event.keyCode === 65 && event.ctrlKey === true) ||
        (event.keyCode === 67 && event.ctrlKey === true) ||
        (event.keyCode === 86 && event.ctrlKey === true) ||
        (event.keyCode === 88 && event.ctrlKey === true)
      ) {
        return;
      }

      // Ensure that it is a number or decimal point
      if (
        (event.shiftKey || event.keyCode < 48 || event.keyCode > 57) &&
        (event.keyCode < 96 || event.keyCode > 105) &&
        event.keyCode !== 190 &&
        event.keyCode !== 110
      ) {
        event.preventDefault();
      }

      // Only allow one decimal point
      if (
        (event.keyCode === 190 || event.keyCode === 110) &&
        event.target.value.indexOf(".") !== -1
      ) {
        event.preventDefault();
      }
    },
    formatBalance(balance) {
      return parseFloat(balance || 0).toFixed(6);
    },

    validateAmount() {
      // Start with a clean errors object to ensure reactivity
      const newErrors = { ...this.errors };

      // Clear amount-specific errors first
      delete newErrors.amount;

      const amount = parseFloat(this.form.amount || 0);

      // Only validate if there's an amount entered
      if (this.form.amount && amount > 0) {
        // Check minimum transaction amount
        if (amount < this.minimumTransaction) {
          newErrors.amount = `Minimum withdrawal amount is ${this.formatBalance(
            this.minimumTransaction
          )} ${this.walletCurrency}`;
        } else {
          const fee = amount * (this.processingFeeRate / 100);
          const totalDeduction = amount + fee;

          if (totalDeduction > this.availableBalance) {
            newErrors.amount =
              "Total amount including fee exceeds available balance";
          }
        }
      }

      // Update errors object to trigger reactivity
      this.errors = newErrors;
    },

    calculateFee() {
      if (!this.form.amount || isNaN(parseFloat(this.form.amount))) {
        return "0.000000";
      }

      const amount = parseFloat(this.form.amount);
      const fee = amount * (this.processingFeeRate / 100);
      return fee.toFixed(6);
    },

    calculateTotal() {
      if (!this.form.amount || isNaN(parseFloat(this.form.amount))) {
        return "0.000000";
      }

      const amount = parseFloat(this.form.amount);
      const fee = amount * (this.processingFeeRate / 100);
      return (amount + fee).toFixed(6);
    },
    validateForm() {
      this.errors = {};
      let isValid = true;

      // Validate amount

      if (!this.form.amount) {
        this.errors.amount = "Amount is required";
        isValid = false;
      } else if (isNaN(parseFloat(this.form.amount))) {
        this.errors.amount = "Amount must be a valid number";
        isValid = false;
      } else if (parseFloat(this.form.amount) <= 0) {
        this.errors.amount = "Amount must be greater than 0";
        isValid = false;
      } else {
        const amount = parseFloat(this.form.amount);
        const fee = amount * (this.processingFeeRate / 100);
        const totalDeduction = amount + fee;

        if (
          totalDeduction > parseFloat(this.walletData?.walletData?.balance || 0)
        ) {
          this.errors.amount =
            "Total amount including fee exceeds available balance";
          isValid = false;
        }
      }

      // Validate email
      if (!this.form.email) {
        this.errors.email = "Email is required";
        isValid = false;
      } else if (!/^\S+@\S+\.\S+$/.test(this.form.email)) {
        this.errors.email = "Please enter a valid email address";
        isValid = false;
      }

      // Validate wallet address
      if (!this.form.walletAddress) {
        this.errors.walletAddress = "Wallet address/ID is required";
        isValid = false;
      }

      return isValid;
    },

    async submitWithdrawal() {
      if (!this.validateForm()) return;

      if (!this.walletData?.user?.verified) {
        this.errorMessage =
          "Your account must be verified before you can withdraw funds.";
        global.app.$toastr.error(this.errorMessage);
        return;
      }

      this.isSubmitting = true;
      this.errorMessage = "";
      this.successMessage = "";

      try {
        const fee = parseFloat(this.calculateFee());
        const total = parseFloat(this.calculateTotal());

        const payload = {
          amount: parseFloat(this.form.amount),
          fee: fee,
          total: total,
          currency: this.walletData?.walletData?.currency || "BTC",
        };

        const response = await this.submitWithdrawalRequest(payload);

        if (response && response.status) {
          global.app.$toastr.success(
            "Withdrawal request submitted successfully!"
          );

          // Redirect to wallet after delay
          setTimeout(() => {
            this.$router.push("/wallet");
          }, 1500);
        } else {
          this.errorMessage =
            response?.message ||
            "Failed to submit withdrawal request. Please try again.";
          global.app.$toastr.error(this.errorMessage);
        }
      } catch (error) {
        console.error("Withdrawal error:", error);
        this.errorMessage =
          "An error occurred while processing your request. Please try again.";
        global.app.$toastr.error(this.errorMessage);
      } finally {
        setTimeout(() => {
          this.isSubmitting = false;
        }, 2000);
      }
    },

    cancelWithdrawal() {
      this.$router.push("/wallet");
    },
  },

  async created() {
    try {
      const response = await this.getWalletData();
      this.walletData = response;

      // Check if user is verified
      if (!this.walletData?.user?.verified) {
        this.errorMessage =
          "Your account must be verified before you can withdraw funds.";
      }

      // Pre-populate form fields
      if (this.walletData?.user?.email) {
        this.form.email = this.walletData.user.email;
      }
      if (this.walletData?.user?.walletAddress) {
        this.form.walletAddress = this.walletData.user.walletAddress;
      }
    } catch (error) {
      console.error("Error fetching wallet data:", error);
      this.errorMessage =
        "Failed to load wallet data. Please refresh the page.";
    }
  },
};
</script>

<style scoped>
.btn.btn-outline-dark.cancel-btn:hover {
  color: #fff !important;
}
.withdraw-section {
  background-color: #fff;
  margin: 30px auto;
  max-width: 800px;
}

.withdraw-form {
  max-width: 600px;
  margin: 0 auto;
}

.border-dashed {
  border-top: 1px dashed #ccc;
}

.summary {
  font-weight: 600;
  margin-bottom: 15px;
}

.withdraw-btn {
  background-color: #fcb913;
  color: #000;
  font-weight: 600;
}

.withdraw-btn:hover {
  background-color: #e6a700;
}

.cancel-btn {
  font-weight: 600;
}

.form-control:disabled {
  background-color: #f8f9fa;
  opacity: 0.65;
  cursor: not-allowed;
}

/* Add to the existing style section */
.amount-input {
  font-size: 1.25rem;
  font-weight: 600;
  color: #2c3e50;
  border: 2px solid #fcb913;
  padding: 12px 16px;
  background: linear-gradient(135deg, #fff9e6 0%, #ffffff 100%);
  transition: all 0.3s ease;
}

.amount-input:focus {
  border-color: #e6a700;
  box-shadow: 0 0 0 0.2rem rgba(252, 185, 19, 0.25);
  background: #ffffff;
}

.amount-input::placeholder {
  color: #a0a0a0;
  font-weight: 500;
}

.amount-label {
  color: #2c3e50;
  font-weight: 700;
  font-size: 1rem;
  margin-bottom: 8px;
}

.balance-info {
  font-weight: 600;
  color: #28a745;
}

.summary-amount {
  font-size: 0.95rem;
  font-weight: 600;
  color: #2c3e50;
}

.total-amount {
  font-size: 1rem;
  font-weight: 700;
  color: #28a745;
}

.fee-amount {
  font-size: 0.95rem;
  font-weight: 600;
  color: #dc3545;
}

.deducted-amount {
  font-size: 0.95rem;
  font-weight: 700;
  color: #fd7e14;
}
</style>
